import { Directive, ElementRef, EventEmitter, HostBinding, HostListener, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

/**
 * Directive that adds click, Enter, and Space key events for accessibility.
 *
 * @remarks
 * Enables keyboard navigation by listening for click, keydown.enter, and keydown.space events
 * on the host element. Provides accessibility support for tab navigation and keyboard interaction.
 * The directive throttles events to prevent rapid-fire triggering, especially useful for
 * spacebar events in Firefox. Automatically adds cursor pointer styling
 * to the host element.
 *
 * @example
 *
 * ```html
 * <div
 *   tabindex="0"
 *   (bbKeyboardClick)="toggle()"
 * >
 *   Click or press Enter/Space to toggle
 * </div>
 * ```
 */
@Directive({
  selector: '[bbKeyboardClick]',
  standalone: false,
})
export class KeyboardClickDirective implements OnDestroy {
  /**
   * Stream that should be fired on click/keydown.enter/keydown.space
   *
   * @example
   *
   * ```html
   * <div (bbKeyboardClick)="toggle()">
   *   Toggleable content
   * </div>
   * ```
   */
  @Output() bbKeyboardClick = new EventEmitter<Event>();

  private readonly emit$ = new Subject<Event>();
  private readonly gc$ = new Subject<void>();
  constructor(private readonly elem: ElementRef<HTMLElement>) {
    // Fix for frequent keydown event generation for spacebar in FF and IE
    this.emit$.pipe(throttleTime(100), takeUntil(this.gc$)).subscribe(this.bbKeyboardClick);
  }
  /**
   * Adds default class to the hosting element. This class adds the cursor pointer style.
   *
   * @internal
   */
  @HostBinding('class')
  protected classes = 'bb-keyboard-click-directive';

  /**
   * Handles keyboard navigation events for Enter and Space keys.
   *
   * @remarks
   * Responds to keydown.enter and keydown.space events on the host element.
   * Validates that the event target is the host element itself (not nested children)
   * before emitting the event. This ensures keyboard navigation behaves correctly
   * for the specific element with the directive.
   *
   * @param $event - The keyboard event triggered by Enter or Space key press
   */
  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onActionKeyPressed($event: KeyboardEvent) {
    // Check if the selected element is the current element itself and not its nested selectable children
    if (this.elem.nativeElement !== $event.target) {
      return;
    }
    this.emit$.next($event);
  }

  /**
   * Handles mouse click events on the host element.
   *
   * @remarks
   * Responds to click events and emits them through the bbKeyboardClick output.
   * Works in conjunction with keyboard event handling to provide consistent
   * interaction behavior for both mouse and keyboard users.
   *
   * @param $event - The mouse click event
   */
  @HostListener('click', ['$event'])
  onClicked($event: MouseEvent) {
    this.emit$.next($event);
  }

  /**
   * Cleans up resources when the directive is destroyed.
   *
   * @remarks
   * Triggers the garbage collection subject to complete any active subscriptions
   * and prevent memory leaks. This ensures proper cleanup of the throttled event
   * stream when the host element is removed from the DOM.
   */
  ngOnDestroy() {
    this.gc$.next();
  }
}
